import { storeToRefs } from 'pinia'
import {
  BackClientListInfo,
  BackPlayerListInfoData,
  BackRealNameInfo,
  ClubConfigData,
  ClubConfigParams,
  ClubCustomerListDataV2,
  ClubMemberFullProfileUpdateRequest,
  ClubMemberFullProfileVo,
  ClubMemberIncomeData,
  ClubMemberListData,
  ClubPlayerListDataV2,
  ClubWaiterListDataV2,
  FilterClientList,
  FilterClubCustomerListV2,
  FilterClubMemberList,
  FilterClubMemberPlayerData,
  FilterClubWaiterListV2,
  FilterPlayerListV2,
  GetUseRealName,
  ManagerSetUserInfo,
  MemberFullProfileParams,
  MemberProfileApprovalsDetail,
  MembersBasicInfoParams,
} from '@/api/clubMember/clubMemberType'
import {
  AuditMemberProfileApprovalsInfo,
  FilterMemberProfileApprovals,
  MemberProfileApprovalsListData,
} from '@/api/clubMember/memberProfileApprovals'
import { useUserStore } from '@/store'
import { EnumIdType } from '@/typings'
import { withCurrentClubId } from '@/utils/club'
import { http } from '@/utils/http'

// 分页查询成员列表
export function requestGetClubMemberList(data: FilterClubMemberList) {
  return http.get<ClubMemberListData>('/clubs/members', data)
}

// 增加身份
export function requestPostAddMemberRole(data: { id: string, role: EnumIdType }) {
  return http.post(`/clubs/members/${data.id}/roles`, data)
}

// 移除身份
export function requestDeleteRemoveMemberRole(data: { id: string, role: EnumIdType }) {
  return http.delete(`/clubs/members/${data.id}/roles/${data.role}`, data)
}

// 移除对象 删除成员
export function requestDeleteRemoveMember(data: { id: string }) {
  return http.delete(`/clubs/members/${data.id}`, data)
}

// 分页查询客户列表
export function requestGetClubClientList(data: FilterClientList) {
  const userStore = useUserStore()
  const { userInfo } = storeToRefs(userStore)
  data.clubId = data?.clubId || userInfo.value?.clubId || ''
  return http.get<BackClientListInfo>('/clubs/members/list-customers', data)
}

// 获取当前登录用户的收入信息
export function requestGetClubMemberIncome() {
  return http.get<ClubMemberIncomeData>('/clubs/members/income')
}

// 获取当前俱乐部陪玩列表
export function requestGetClubMemberPlayerList(data: FilterClubMemberPlayerData) {
  const userStore = useUserStore()
  const { userInfo } = storeToRefs(userStore)
  data.clubId = data?.clubId || userInfo.value?.clubId || ''
  return http.get<BackPlayerListInfoData>('/clubs/members/players', data)
}

// 获取用户实名认证、绑定银行卡的相关信息
export function requestGetUserRealNameInfo(data: GetUseRealName) {
  return http.get<BackRealNameInfo>(
    `/club/members/me/identity`,
    data,
  )
}

// 账户封禁
export function requestGetClubMemberBan(memberId: string) {
  return http.post(`/clubs/members/${memberId}/ban`)
}

// 账户解封
export function requestGetClubMemberUnban(memberId: string) {
  return http.post(`/clubs/members/${memberId}/unban`)
}

// 获取当前俱乐部所有的陪玩列表V2
export function requestGetPlayerListV2(data: FilterPlayerListV2) {
  const userStore = useUserStore()
  const { userInfo } = storeToRefs(userStore)
  data.clubId = data?.clubId || userInfo.value?.clubId || ''
  return http.get<ClubPlayerListDataV2>(
    '/clubs/members/players/v2',
    data,
  )
}
// 获取俱乐部配置
export function requestGetClubConfig() {
  return http.get<ClubConfigData>(
    `/configs/clubs/config`,
  )
}

// 更新俱乐部配置
export function requestPostUpdateClubConfig(data: ClubConfigParams) {
  return http.put(
    `/configs/clubs/config`,
    data,
  )
}

// 获取当前俱乐部所有的接待列表V2
export function requestGetWaitersListV2(data: FilterClubWaiterListV2) {
  const userStore = useUserStore()
  const { userInfo } = storeToRefs(userStore)
  data.clubId = data?.clubId || userInfo.value?.clubId || ''
  return http.get<ClubWaiterListDataV2>(
    '/clubs/members/waiters/v2',
    data,
  )
}

// 获取当前俱乐部所有的顾客列表V2
export function requestGetCustomerListV2(data: FilterClubCustomerListV2) {
  const userStore = useUserStore()
  const { userInfo } = storeToRefs(userStore)
  data.clubId = data?.clubId || userInfo.value?.clubId || ''
  return http.get<ClubCustomerListDataV2>(
    '/clubs/members/customers/v2',
    data,
  )
}

// 管理员设置用户信息
export function requestPostManagerSetUserInfo(data: ManagerSetUserInfo) {
  return http.put(
    `/clubs/members/profile/${data.memberId}`,
    data,
  )
}

// 分页查询审批记录列表
export function requestGetMemberProfileApprovalsList(data: FilterMemberProfileApprovals) {
  const params = withCurrentClubId(data)

  return http.get<MemberProfileApprovalsListData>(
    `/clubs/members/profile-approvals`,
    params,
  )
}

// 审核成员信息修改申请
export function requestPostAuditMemberProfileApprovals(data: AuditMemberProfileApprovalsInfo) {
  return http.post(
    `/clubs/members/profile-approvals/${data.approvalId}/audit`,
    data,
  )
}

// 获取老板信息详情
export function requestGetMemberProfileApprovalsDetail(memberId: string) {
  return http.get<MemberProfileApprovalsDetail>(
    `/clubs/members/${memberId}/basic-info`,
  )
}

//
export function requestPostUpdateMemberProfileApprovalsDetail(memberId: string, data: MembersBasicInfoParams) {
  return http.put(
    `/clubs/members/${memberId}/basic-info`,
    data,
  )
}

// 查询用户系统内消费额度
export function requestGetInnerComsumeList(customerId: string) {
  return http.get<string>(
    `/clubs/members/${customerId}/inner-comsume`,
  )
}

// 转移超管权限
export function requestPostTransferSuperAdmin(memberId: string) {
  return http.post(
    `/clubs/transfer`,
    {},
    { memberId },
  )
}
// 查询成员完整资料
export function requestGetMemberFullProfile(memberId: string, params: MemberFullProfileParams) {
  return http.get<ClubMemberFullProfileVo>(
    `/clubs/members/${memberId}/full-profile`,
    params,
  )
}

// 更新成员完整资料
export function requestPostUpdateMemberFullProfile(memberId: string, data: ClubMemberFullProfileUpdateRequest) {
  return http.put(
    `/clubs/members/${memberId}/full-profile`,
    data,
  )
}
